import React, { useMemo } from 'react'
import { simpleHash } from './simpleHash.js'
import getKeyPropsString from './getKeyPropsString.js'
import { Grid, GridItem } from '../components/grid.js'
import { Container } from '../components/container.js'
import ErrorBoundary from './ErrorBoundary.js'

const colContentClasses: Record<number, string> = {
  12: 'col-start-1 col-end-13',
  10: 'col-start-2 col-end-12',
  8: 'col-start-3 col-end-11',
  6: 'col-start-4 col-end-10',
  4: 'col-start-5 col-end-9',
} as const

// 预定义带 tablet 前缀的类名，确保 Tailwind 可以识别
const colContentClassesTablet: Record<number, string> = {
  12: 'tablet:col-start-1 tablet:col-end-13',
  10: 'tablet:col-start-2 tablet:col-end-12',
  8: 'tablet:col-start-3 tablet:col-end-11',
  6: 'tablet:col-start-4 tablet:col-end-10',
  4: 'tablet:col-start-5 tablet:col-end-9',
} as const

export interface ContainerProps {
  container?: {
    /** 桌面端列内容宽度 */
    desktopSpan: number
    /** 移动端列内容宽度 */
    mobileSpan: number
  }
}

export interface StylesProps {
  /** 自定义样式字符串或对象 */
  style?: string | React.CSSProperties
  /** 唯一标识符 */
  uid?: string
  /** 是否禁用 */
  disabled?: boolean
}

/**
 * 根据 props 生成确定性 uid
 */
const generateUID = (props: Record<string, any>) => {
  const keyString = getKeyPropsString(props)
  return `style_${simpleHash(keyString)}`
}

/**
 * 从组件 props 中提取样式相关属性
 */
export const getStylesProps = <T extends Record<string, unknown>>(props: T & StylesProps): StylesProps => {
  const { style, uid, disabled } = props
  return { style, uid, disabled }
}

/**
 * 样式注入组件
 */
const Styles: React.FC<StylesProps> = ({ style, uid = generateUID({}), disabled = false }) => {
  if (!style || disabled) {
    return null
  }

  return (
    <style
      suppressHydrationWarning
      data-style-id={uid}
      dangerouslySetInnerHTML={{ __html: `.block-${uid} { ${style} }` }}
    />
  )
}

/**
 * 样式 Hook，返回样式类名和样式组件
 * @param styleProps - 样式属性
 */
export const useStyles = (styleProps: Partial<StylesProps>, data?: Record<string, any>) => {
  const uid = useMemo(() => styleProps?.uid || generateUID({ ...styleProps, data }), [data, styleProps])

  const StyleComponent = useMemo(() => <Styles {...styleProps} uid={uid} />, [styleProps, uid])

  return {
    className: `block-${uid} `,
    StyleComponent,
    uid,
  } as const
}

const getContainerClassName = (props?: Partial<StylesProps & ContainerProps>) => {
  const { desktopSpan, mobileSpan } = props?.container || {}
  const mobileClasses = mobileSpan ? colContentClasses[mobileSpan] : ''
  const desktopClasses = desktopSpan ? colContentClassesTablet[desktopSpan] : ''
  return `${mobileClasses} ${desktopClasses}`.trim()
}

/**
 * 标记组件是否已被 withLayout 包装
 */
const WRAPPED_COMPONENT_SYMBOL = Symbol('withLayoutWrapped')

/**
 * 高阶组件通过 withLayout 包装组件，自动应用样式和容器布局
 *
 * @param WrappedComponent - 需要包装的组件
 * @param defaultLayoutProps - 默认样式属性
 */
export function withLayout<P>(
  WrappedComponent: React.ComponentType<P>,
  defaultLayoutProps?: Partial<StylesProps & ContainerProps>
) {
  // 防止重复包装
  if ((WrappedComponent as any)[WRAPPED_COMPONENT_SYMBOL]) {
    return WrappedComponent as any
  }
  // 使用您的简洁方法：直接用泛型保留类型
  const withLayout = React.forwardRef<
    any,
    P & { style?: string | React.CSSProperties; container?: ContainerProps['container'] }
  >((props, ref) => {
    const propsStyleProps = getStylesProps(props)

    // 将 style 对象转换为字符串
    const styleToString = (style?: string | React.CSSProperties): string => {
      if (!style) return ''
      if (typeof style === 'string') return style
      if (typeof style === 'object' && Object.entries(style).length <= 0) return ''
      return (
        Object.entries(style)
          .map(([key, value]) => {
            const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase()
            return `${cssKey}: ${value}`
          })
          .join('; ') + ';'
      )
    }

    // 合并 style 字段，避免覆盖，确保每个样式都以分号结尾
    const mergedStyle = [defaultLayoutProps?.style, propsStyleProps?.style]
      .map(styleToString)
      .filter((style): style is string => Boolean(style))
      .map(style => (style.trim().endsWith(';') ? style : `${style};`))
      .join(' ')

    const finalStyleProps = {
      ...defaultLayoutProps,
      ...propsStyleProps,
      ...(mergedStyle && { style: mergedStyle }),
    }

    const { className, StyleComponent } = useStyles(finalStyleProps, (props as any)?.data)

    const WithContainerWrapper = (props: any) => {
      return (
        <Container className={`${className} ${(props as any)?.className || ''}`.trim()}>
          <Grid className="container_grid">
            <GridItem className={getContainerClassName(props)}>
              <WrappedComponent {...props} ref={ref} />
            </GridItem>
          </Grid>
        </Container>
      )
    }

    const WithoutContainerWrapper = (props: any) => {
      return <WrappedComponent {...props} className={`${className} ${props?.className || '' || ''}`.trim()} ref={ref} />
    }

    // @ts-ignore
    // eslint-disable-next-line no-unused-vars
    const { style: _style, ...withOutStyleProps } = props

    return (
      <>
        <ErrorBoundary>
          {StyleComponent}
          {Reflect.ownKeys((withOutStyleProps as any)?.container || {})?.length > 0
            ? WithContainerWrapper(withOutStyleProps)
            : WithoutContainerWrapper(withOutStyleProps)}
        </ErrorBoundary>
      </>
    )
  })

  withLayout.displayName = `WithLayout${WrappedComponent.displayName || WrappedComponent.name || 'Component'}`

  // 标记组件已被包装
  ;(withLayout as any)[WRAPPED_COMPONENT_SYMBOL] = true
  return withLayout
}
